import type {
  ExactMatchAddress,
  NearbyAddress,
  PartialAddress,
  SiteAddress,
} from "@/.generated/hey/backoffice-backend/types.gen";
import type {
  IExactMatchAddressResponse,
  INearbyAddressResponse,
  IPartialAddressResponse,
  TReplaceSiteAddressResponse,
  TSiteAddressResponse,
} from "@/api/backoffice-v2/backoffice-payloads";

/**
 * Maps SDK SiteAddressesSiteAddress to our TSiteAddressResponse
 */
export function mapSiteAddress(sdkAddress: SiteAddress): TSiteAddressResponse {
  switch (sdkAddress.geocodingStatus) {
    case "GEOCODED":
      return mapExactMatchAddress(sdkAddress);
    case "NEARBY":
      return mapNearbyAddress(sdkAddress);
  }
}

/**
 * Maps SDK response (which can include partial addresses) to our TReplaceSiteAddressResponse
 */
export function mapReplaceSiteAddress(sdkAddress: SiteAddress | PartialAddress): TReplaceSiteAddressResponse {
  switch (sdkAddress.geocodingStatus) {
    case "GEOCODED":
      return mapExactMatchAddress(sdkAddress);
    case "NEARBY":
      return mapNearbyAddress(sdkAddress);
    case "NON_GEOCODED":
      return mapPartialAddress(sdkAddress);
  }
}

function mapExactMatchAddress(sdkAddress: ExactMatchAddress): IExactMatchAddressResponse {
  return {
    geocodingStatus: "GEOCODED",
    street: sdkAddress.street,
    streetNumber: sdkAddress.streetNumber,
    addressLine1: sdkAddress.addressLine1,
    zipCode: sdkAddress.zipCode,
    city: sdkAddress.city,
    country: sdkAddress.country,
    geocodedFields: {
      externalId: sdkAddress.geocodedFields.externalId,
      score: sdkAddress.geocodedFields.score,
      administrativeDivisions: sdkAddress.geocodedFields.administrativeDivisions,
      cityCode: sdkAddress.geocodedFields.cityCode,
      latitude: sdkAddress.geocodedFields.latitude,
      longitude: sdkAddress.geocodedFields.longitude,
      provider: sdkAddress.geocodedFields.provider,
    },
    complement: sdkAddress.complement ?? null,
    digicode: sdkAddress.digicode ?? null,
    building: sdkAddress.building ?? null,
    level: sdkAddress.level ?? null,
    stairway: sdkAddress.stairway ?? null,
  };
}

function mapNearbyAddress(sdkAddress: NearbyAddress): INearbyAddressResponse {
  return {
    geocodingStatus: "NEARBY",
    addressLine1: sdkAddress.addressLine1,
    zipCode: sdkAddress.zipCode,
    city: sdkAddress.city,
    country: sdkAddress.country,
    nearbyFields: {
      externalId: sdkAddress.nearbyFields.externalId,
      streetNumber: sdkAddress.nearbyFields.streetNumber,
      street: sdkAddress.nearbyFields.street,
      zipCode: sdkAddress.nearbyFields.zipCode,
      city: sdkAddress.nearbyFields.city,
      score: sdkAddress.nearbyFields.score,
      latitude: sdkAddress.nearbyFields.latitude,
      longitude: sdkAddress.nearbyFields.longitude,
      cityCode: sdkAddress.nearbyFields.cityCode,
      administrativeDivisions: sdkAddress.nearbyFields.administrativeDivisions,
      provider: sdkAddress.nearbyFields.provider,
    },
    complement: sdkAddress.complement ?? null,
    digicode: sdkAddress.digicode ?? null,
    building: sdkAddress.building ?? null,
    level: sdkAddress.level ?? null,
    stairway: sdkAddress.stairway ?? null,
  };
}

function mapPartialAddress(sdkAddress: PartialAddress): IPartialAddressResponse {
  return {
    geocodingStatus: "NON_GEOCODED",
    addressLine1: sdkAddress.addressLine1,
    zipCode: sdkAddress.zipCode,
    city: sdkAddress.city,
    country: sdkAddress.country,
    complement: sdkAddress.complement ?? null,
    digicode: sdkAddress.digicode ?? null,
    building: sdkAddress.building ?? null,
    level: sdkAddress.level ?? null,
    stairway: sdkAddress.stairway ?? null,
  };
}
